// path: src/utils/endlessScore.js
// Endless-only weighted scoring (Story has no score).

export const ENDLESS_SCORE_BOARD_SIZES = [4, 9, 16];

export const ENDLESS_SCORE_BASE_BY_BOARD = {
    4: 5, // 2×2
    9: 10, // 3×3
    16: 15, // 4×4
};

export const ENDLESS_SCORE_MODIFIER_KEYS = [
    "twistedMode",
    "shyCorgisMode",
    "anxiousMode",
    "hardcoreMode",
    "blitzMode",
];

export function getEndlessScoreBasePoints(boardSize) {
    const size = Number(boardSize);
    return ENDLESS_SCORE_BASE_BY_BOARD[size] || 5;
}

export function countEnabledEndlessScoreModifiers(modifiers = {}) {
    return ENDLESS_SCORE_MODIFIER_KEYS.reduce((count, key) => {
        return count + (modifiers?.[key] ? 1 : 0);
    }, 0);
}

/**
 * Additive ×2 per enabled mechanic. 0 mechanics => 1×.
 * 1=>2, 2=>4, 3=>6, 4=>8
 */
export function getEndlessScoreModifierMultiplier(modifiers = {}) {
    const enabled = countEnabledEndlessScoreModifiers(modifiers);
    if (enabled <= 0) {
        return 1;
    }
    return enabled * 2;
}

export function getEndlessPointsPerClick(boardSize, modifiers = {}) {
    const base = getEndlessScoreBasePoints(boardSize);
    const multiplier = getEndlessScoreModifierMultiplier(modifiers);
    return base * multiplier;
}

export function buildEndlessScoreModifiersFromRunConfig(runConfig = null) {
    const excluded = runConfig?.scoreExcludedModifiers || {};
    return {
        twistedMode: Boolean(runConfig?.twistedMode),
        shyCorgisMode:
            Boolean(runConfig?.shyCorgisMode) && !excluded.shyCorgisMode,
        anxiousMode: Boolean(runConfig?.anxiousMode) && !excluded.anxiousMode,
        hardcoreMode: Boolean(runConfig?.hardcoreMode),
        blitzMode: Boolean(runConfig?.blitzMode),
    };
}

/**
 * Preview strip for Endless setup / HUD: pts per correct pick, level pace, hearts.
 * Blitz forces Hardcore for both scoring and hearts.
 */
export function buildEndlessScorePreview(boardSize, options = {}) {
    const size = Number(boardSize);
    const safeSize = [4, 9, 16].includes(size) ? size : 4;
    const blitzMode = Boolean(options.blitzMode);
    const modifiers = {
        twistedMode: Boolean(options.twistedMode),
        shyCorgisMode: Boolean(options.shyCorgisMode),
        anxiousMode: Boolean(options.anxiousMode),
        hardcoreMode: blitzMode ? true : Boolean(options.hardcoreMode),
        blitzMode,
    };
    const base = getEndlessScoreBasePoints(safeSize);
    const multiplier = getEndlessScoreModifierMultiplier(modifiers);
    const pointsPerClick = base * multiplier;
    const modifierCount = countEnabledEndlessScoreModifiers(modifiers);
    const heartsAllowed = !modifiers.hardcoreMode && !blitzMode;

    return {
        boardSize: safeSize,
        base,
        multiplier,
        pointsPerClick,
        picksPerLevel: safeSize,
        modifierCount,
        heartsAllowed,
        modifiers,
        treatRatePercent: getEndlessTreatRatePercent(safeSize),
        summary: heartsAllowed
            ? `${pointsPerClick} pts/pick · level every ${safeSize} picks · hearts allowed`
            : `${pointsPerClick} pts/pick · level every ${safeSize} picks · Hardcore (no hearts)`,
    };
}

/** Board-size Treat conversion from Endless Score (2×2 / 3×3 / 4×4). */
export const ENDLESS_TREAT_RATE_PERCENT_BY_BOARD = {
    4: 33,
    9: 66,
    16: 100,
};

/** Unfinished board when the run ends pays half the board-size rate. */
export const ENDLESS_INCOMPLETE_BOARD_TREAT_FACTOR = 0.5;

export function getEndlessTreatRatePercent(boardSize) {
    const size = Number(boardSize);
    return ENDLESS_TREAT_RATE_PERCENT_BY_BOARD[size] || 33;
}

/**
 * Endless Treats from Score: completed boards at full board-size rate,
 * leftover picks on the unfinished board at 50% of that rate.
 */
export function getEndlessTreatsFromScore({
    score = 0,
    levelsCompleted = 0,
    boardSize = 4,
    modifiers = {},
} = {}) {
    const size = [4, 9, 16].includes(Number(boardSize))
        ? Number(boardSize)
        : 4;
    const totalScore = Math.max(0, Math.floor(Number(score) || 0));
    const levels = Math.max(0, Math.floor(Number(levelsCompleted) || 0));
    const pointsPerClick = getEndlessPointsPerClick(size, modifiers);
    const completedScore = Math.min(
        totalScore,
        levels * size * pointsPerClick
    );
    const partialScore = Math.max(0, totalScore - completedScore);
    const ratePercent = getEndlessTreatRatePercent(size);

    const completedTreats = Math.floor((completedScore * ratePercent) / 100);
    const partialAtFullRate = Math.floor((partialScore * ratePercent) / 100);
    const partialTreats = Math.floor(
        partialAtFullRate * ENDLESS_INCOMPLETE_BOARD_TREAT_FACTOR
    );

    return completedTreats + partialTreats;
}
